import { isLogLevelString, isObject, isSentryDsn } from './validation.mjs';
import { Logger } from './Logger.mjs';
import { SentryTransport } from './transports/SentryTransport.mjs';
import * as sentryModule from '@sentry/browser';
import * as integrationsModule from '@sentry/integrations';

// since this module may be transpiled, we don't have control over how @sentry dependencies are loaded
// so, we look for ECMA style import at 'default' and fall back to require style named exports if not present
/* c8 ignore next 2 */
const sentry = sentryModule;
const Integrations = integrationsModule;

/**
 * SentryLogger class.
 *
 * Has same interface as parent Logger class, but allows for Sentry SDK initialization in initialize() and has
 * default configuration to use SentryTransport to deliver events to Sentry service.
 *
 * @class
 * @method child
 * @method debug
 * @method deregisterNodes
 * @method error
 * @method info
 * @method log
 * @method registerNodes
 * @method warn
 * @static defaultOpts
 * @static initialize
 */
class SentryLogger extends Logger {
  /** @constructor */
  constructor(opts) {
    super(opts);
  }

  /**
   * Static factory method for class.
   * In addition to returning the SentryLogger instance, this method also triggers the Sentry.init() actions which
   * starts all tracking
   *
   * @param {SentryLoggerOptions} opts
   * @returns {SentryLogger}
   */
  static initialize(opts = {}) {
    /* c8 ignore next */
    const config = opts.sentry || {};

    const integrations = [
      // turns off Sentry unhandled exception / rejection capture
      // these should be enforced as appropriate in application
      new sentry.Integrations.GlobalHandlers({
        onerror: false,
        onunhandledrejection: false
      }),
      // captures any non-standard properties on Error objects
      new Integrations.ExtraErrorData()
    ];

    if (config.captureConsole) {
      integrations.push( new Integrations.CaptureConsole(config.captureConsole) );
    }

    if (config.rewriteFrames) {
      integrations.push( new Integrations.RewriteFrames(config.rewriteFrames) );
    }

    config.integrations = integrations;

    sentry.init(config);

    /* c8 ignore next */
    return new this(opts);
  }

  /**
   * Method to statically determine if a set of options meets validation criteria for the class.
   *
   * @param {SentryLoggerOptions} opts
   */
  static validateOpts(opts) {
    super.validateOpts(opts);

    if (!isObject(opts.sentry)) {
      throw new TypeError('Invalid or missing sentry option');
    }

    if (!isSentryDsn(opts.sentry.dsn)) {
      throw new TypeError('Invalid or missing sentry.dsn option');
    }

    if (opts.sentry.captureConsole) {
      if (
        !isObject(opts.sentry.captureConsole)
        || !Array.isArray(opts.sentry.captureConsole.levels)
        || !opts.sentry.captureConsole.levels.every(isLogLevelString)
      ) {
        throw new TypeError('Invalid sentry.captureConsole option');
      }
    }
  }
}

/**
 * The Sentry config can take any option available to Sentry.init()
 *
 * @typedef SentryConfig
 * @prop {Object} [captureConsole] configuration for CaptureConsole integration. If specified, an array of capture log
 * levels must be passed via 'levels' property. Note that if captureConsole is enabled (as it is by default), that this
 * capture and event upload to Sentry takes place outside the scope / visibility of the SentryLogger instance.
 * @prop {string} dsn The Sentry DSN used as target endpoint for the application
 * @prop {string} [environment] environment identifier used by Sentry to distinguish events from different
 * application environments
 * @prop {string} [release] release identifier used by Sentry to associate events with a given release
 *
 * SentryLogger options are a superset of Logger options, with default usage of SentryTransport for log event delivery,
 * with an added 'sentry' property specifying the sentry configuration.
 *
 * @typedef SentryLoggerOptions
 * @prop {Object} [context] key-value map of stored context information
 * @prop {string} [defaultLogLevel] setting for log level to be applied to events that do not have specified level
 * (for example, if calling .log() method directly)
 * @prop {string} [level] Threshold log level for the logger. For example, 'warn' would log for 'error' and 'warn',
 * while 'debug' would log for all levels
 * @prop {SentryConfig} [sentry] Sentry-specific configuration
 * @prop {Transport[]} transports Array of Transport objects to be used as listeners to log events
 *
 * @type SentryLoggerOptions
 */
SentryLogger.defaultOpts = Object.freeze({
  context: {},
  defaultLogLevel: 'debug',
  level: 'warn',
  sentry: {
    captureConsole: {
      levels: ['error', 'warn']
    },
    dsn: null,
    environment: null,
    release: null
  },
  transports: [
    SentryTransport.initialize()
  ]
});

export {
  SentryLogger as default,
  SentryLogger
};
